import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import { storageService } from '@/services/firebase/storage.service'

// Nombre exacto del archivo en la raíz de Firebase Storage. Mantener literal —
// los espacios y mayúsculas hacen parte del path real.
const HYMN_STORAGE_PATH = 'Himno JECARO.mp4'

// Pila tipográfica serif usada en lema y letra del himno para dar carácter
// editorial/clásico sin agregar dependencia de webfont.
const SERIF_STACK = 'Georgia, "Times New Roman", serif'

const CARD_SX = {
  flex: 1,
  border: 'none',
  borderRadius: { xs: 2, md: 3 },
  p: { xs: 2.5, sm: 3, md: 3.5, lg: 4 },
  backgroundColor: '#fff',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: { md: 'translateY(-6px)' },
    boxShadow: { md: '0 12px 32px rgba(0,0,0,0.10)' },
  },
} as const

const SECTION_TITLE_SX = {
  color: 'primary.dark',
  fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem', lg: '1.625rem' },
  mb: 0.75,
} as const

const BODY_SX = {
  fontSize: { xs: '0.875rem', sm: '0.938rem', md: '1rem' },
  lineHeight: { xs: 1.65, md: 1.75 },
  textAlign: 'justify' as const,
}

const VERSE_SX = {
  fontFamily: SERIF_STACK,
  fontStyle: 'italic' as const,
  fontSize: { xs: '0.938rem', sm: '1rem' },
  lineHeight: 1.85,
  whiteSpace: 'pre-line' as const,
  color: 'text.secondary',
}

const IMAGE_FRAME_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #FAFBFC 0%, #F0F2F5 100%)',
  borderRadius: { xs: 1.5, md: 2 },
  p: { xs: 2, md: 3 },
  mb: { xs: 2, md: 3 },
  minHeight: { xs: 220, md: 280 },
}

const IMAGE_SX = {
  maxHeight: { xs: 200, md: 260 },
  maxWidth: '100%',
  objectFit: 'contain' as const,
  filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.12))',
}

interface SwatchProps {
  color: string
  label: string
  border?: boolean
}

const Swatch: FC<SwatchProps> = ({ color, label, border }) => (
  <Stack alignItems="center" spacing={0.75}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1,
        backgroundColor: color,
        border: border ? '1px solid #E0E0E0' : 'none',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
      }}
    />
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}
    >
      {label}
    </Typography>
  </Stack>
)

const InstitutionalSymbols: FC = () => {
  const [hymnUrl, setHymnUrl] = useState<string | null>(null)
  const [hymnLoading, setHymnLoading] = useState(true)
  const [hymnError, setHymnError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    storageService
      .getFileURL({ path: HYMN_STORAGE_PATH })
      .then((url) => {
        if (cancelled) return
        setHymnUrl(url)
        setHymnLoading(false)
      })
      .catch((err: Error) => {
        if (cancelled) return
        setHymnError(err.message || 'No se pudo cargar el video del himno.')
        setHymnLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 6, sm: 7, md: 8 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Header sección con accent gold */}
      <Box
        sx={{
          textAlign: 'center',
          mb: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h2"
          fontWeight={700}
          gutterBottom
          sx={{
            color: 'primary.dark',
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2rem', lg: '2.25rem' },
            mb: { xs: 2, md: 2.5 },
          }}
        >
          Símbolos Institucionales
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: { xs: '100%', sm: 600, md: 720 },
            mx: 'auto',
            fontSize: { xs: '0.875rem', sm: '0.938rem', md: '1rem' },
            lineHeight: { xs: 1.6, md: 1.7 },
          }}
        >
          El escudo, la bandera y el himno encarnan la identidad y los valores de la
          Institución Educativa José Eusebio Caro.
        </Typography>
      </Box>

      {/* Escudo + Bandera */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        sx={{ mb: { xs: 3, md: 4 } }}
      >
        {/* Escudo */}
        <Card elevation={0} sx={CARD_SX}>
          <Box sx={IMAGE_FRAME_SX}>
            <Box
              component="img"
              src="/images/Logo.jpg"
              alt="Escudo de la Institución Educativa José Eusebio Caro"
              sx={IMAGE_SX}
            />
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ ...SECTION_TITLE_SX, mb: { xs: 2, md: 2.5 } }}>
            El Escudo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ...BODY_SX, mb: 2 }}>
            Tiene forma de corazón, símbolo de vida. Como figura central hay una
            antorcha encendida; el fuego es el símbolo de la primavera, de la juventud
            y de la tradición cristiana.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ...BODY_SX, mb: 2 }}>
            Al pie de la antorcha aparecen tres libros sobre los cuales descansa la
            figura de un búho. El libro es la figura representativa de la ciencia, de
            la virtud y de la dedicación. En los libros están la tradición, la
            historia y los recuerdos de otras épocas.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ...BODY_SX, mb: 3 }}>
            El búho, animal que permanece con el ceño fruncido, denota la
            concentración e inteligencia. Al otro lado de la antorcha aparece el
            mapamundi mostrando a América con su majestuosa cordillera, escenario de
            belleza natural. Allí está Colombia y en Colombia se levanta Popayán,
            ciudad noble e ilustre.
          </Typography>

          {/* Lema institucional */}
          <Box
            sx={{
              mt: 2,
              py: { xs: 2, md: 2.5 },
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: SERIF_STACK,
                fontStyle: 'italic',
                fontWeight: 600,
                color: 'primary.dark',
                fontSize: { xs: '0.938rem', sm: '1rem', md: '1.063rem' },
                letterSpacing: '0.15em',
                mb: 0.75,
              }}
            >
              VITAE MORES EX CORDE TRADIMUS
            </Typography>
            <Typography
              sx={{
                fontFamily: SERIF_STACK,
                fontStyle: 'italic',
                color: 'text.secondary',
                fontSize: { xs: '0.813rem', md: '0.875rem' },
              }}
            >
              «Las costumbres de la vida brotan del corazón»
            </Typography>
          </Box>
        </Card>

        {/* Bandera */}
        <Card elevation={0} sx={CARD_SX}>
          <Box sx={IMAGE_FRAME_SX}>
            <Box
              component="img"
              src="/images/bandera.png"
              alt="Bandera de la Institución Educativa José Eusebio Caro"
              sx={IMAGE_SX}
            />
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ ...SECTION_TITLE_SX, mb: { xs: 2, md: 2.5 } }}>
            La Bandera
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ...BODY_SX, mb: 2 }}>
            Ostenta tres franjas horizontales de igual tamaño. Sus colores son: amarillo
            oro, azul y amarillo. En el centro contiene, sobre un círculo de fondo
            blanco, el escudo institucional.
          </Typography>

          {/* Swatch de colores */}
          <Stack
            direction="row"
            spacing={{ xs: 2.5, sm: 3 }}
            justifyContent="center"
            sx={{ my: 2.5 }}
          >
            <Swatch color="#f0b800" label="Oro" />
            <Swatch color="#303a63" label="Azul" />
            <Swatch color="#ffffff" label="Blanco" border />
          </Stack>

          <Typography
            variant="subtitle2"
            sx={{
              color: 'primary.dark',
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '0.938rem', md: '1rem' },
            }}
          >
            Significado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ...BODY_SX, mb: 1.5 }}>
            <strong>El oro</strong>, el más noble de los metales, denota la riqueza de
            los materiales y del espíritu.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ...BODY_SX, mb: 1.5 }}>
            <strong>El azul</strong> simboliza la serenidad y la firmeza en el carácter.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={BODY_SX}>
            <strong>El círculo blanco</strong> simplifica el sentido de la vida, la
            visión de las cosas simples, siempre cercanas por la pureza del aire.
          </Typography>
        </Card>
      </Stack>

      {/* Himno — full width, video centrado arriba + letra en grid */}
      <Card elevation={0} sx={{ ...CARD_SX, pb: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: { xs: 'left', md: 'center' }, mb: { xs: 2, md: 3 } }}>
          <Typography variant="h5" fontWeight={700} sx={{ ...SECTION_TITLE_SX, mb: { xs: 1, md: 1.5 } }}>
            Himno Institucional
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.813rem', md: '0.875rem' },
            }}
          >
            <strong>Letra:</strong> Guillermo León Paz Quijano
            <Box component="span" sx={{ mx: 1.5, color: 'secondary.main' }}>•</Box>
            <strong>Música:</strong> Segundo Pabón
          </Typography>
        </Box>

        {/* Video centrado, max 720px */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 3, md: 4 },
          }}
        >
          {hymnLoading && (
            <Box
              sx={{
                width: '100%',
                maxWidth: 720,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: { xs: 220, sm: 280, md: 360 },
                backgroundColor: '#000',
                borderRadius: { xs: 1.5, md: 2 },
              }}
            >
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}

          {!hymnLoading && hymnError && (
            <Alert severity="warning" sx={{ borderRadius: 2, width: '100%', maxWidth: 720 }}>
              No se pudo cargar el video del himno en este momento.
            </Alert>
          )}

          {!hymnLoading && !hymnError && hymnUrl && (
            <Box
              component="video"
              src={hymnUrl}
              controls
              preload="metadata"
              playsInline
              poster="/images/Logo.jpg"
              sx={{
                width: '100%',
                maxWidth: 720,
                maxHeight: { xs: 240, md: 405 },
                borderRadius: { xs: 1.5, md: 2 },
                backgroundColor: '#000',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'block',
              }}
            />
          )}
        </Box>

        {/* Divider ornamental entre video y letra */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            my: { xs: 2.5, md: 3 },
          }}
        >
          <Box sx={{ flex: 1, maxWidth: 120, height: '1px', backgroundColor: 'secondary.main' }} />
          <Typography sx={{ color: 'secondary.main', fontSize: '1rem', lineHeight: 1 }}>
            ❖
          </Typography>
          <Box sx={{ flex: 1, maxWidth: 120, height: '1px', backgroundColor: 'secondary.main' }} />
        </Box>

        {/* Coro centrado sobre las dos columnas de estrofas */}
        <Box
          sx={{
            mx: 'auto',
            maxWidth: 720,
            mb: { xs: 3, md: 4 },
            textAlign: 'center',
            px: { xs: 2, md: 3 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'primary.dark',
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.75rem',
              display: 'block',
              mb: 0.5,
            }}
          >
            Coro
          </Typography>
          <Box
            sx={{
              width: 32,
              height: 2,
              backgroundColor: 'secondary.main',
              borderRadius: 999,
              mx: 'auto',
              mb: 1.5,
            }}
          />
          <Typography sx={{ ...VERSE_SX, textAlign: 'center' }}>
            {`La virtud, el trabajo y la ciencia
en el mundo nos lleva a triunfar.
Estudiantes, tomemos conciencia,
que la vida es vencer y es amar. (bis)`}
          </Typography>
        </Box>

        {/* Estrofas en grid 2x2 (md+) — alineadas con el video; texto centrado
            dentro de cada columna para coherencia con el coro. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 2.5, md: 5 },
            mx: 'auto',
            maxWidth: 720,
            mb: { xs: 4, md: 6 },
          }}
        >
          {[
            {
              titulo: 'I',
              texto: `Compañeros el mundo presente
nos impulsa a un mañana mejor.
Nos enseña una vida consciente
a luchar y a vivir con honor.`,
            },
            {
              titulo: 'II',
              texto: `Ciudadanos con ánimo logremos
la excelencia y un gran corazón,
y en las manos la dicha
de cumplir el deber con pasión.`,
            },
            {
              titulo: 'III',
              texto: `Los valores que a diario aprendemos
nos conducen a un buen convivir;
en el alma semillas sembremos
pues mañana nos han de servir.`,
            },
            {
              titulo: 'IV',
              texto: `Caminemos la ruta trazada
por ancestros de ciencia y de bien.
Caminemos la senda adornada
por las flores hermosas del bien.`,
            },
          ].map((estrofa) => (
            <Box key={estrofa.titulo} sx={{ textAlign: 'center' }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 700,
                  letterSpacing: 2,
                  fontSize: '0.813rem',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {estrofa.titulo}
              </Typography>
              <Typography sx={{ ...VERSE_SX, textAlign: 'center' }}>
                {estrofa.texto}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>
    </Container>
  )
}

export default InstitutionalSymbols
